
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// types
import { SettingsContractType } from '@/new-services/types/settings-contract-types'
import { GatewayRoleT } from '@/core/types/gateway.types'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'

// validation
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

// classes
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        TheButton,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<SettingsContractType>,
            default: null,
        },
    },
    emits: ['create', 'update', 'cancel'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            name: null as string | null,
            isMain: false,
            isConfidential: false,
            roleIds: [] as number[],
        })

        const data_state = reactive({
            roles: new DataState<GatewayRoleT>({}),
        })

        let state_form$ = useVuelidate(
            {
                name: { required },
                roleIds: { required: requiredIf(() => state.isConfidential) },
            },
            state
        )

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление типа договора'
            if (props.isUpdate) return 'Редактирование типа договора'
            if (props.isDelete) return 'Удаление типа договора'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        // roles

        const loadRoles = async (page: number, name?: string) => {
            data_state.roles.pagination.current_page = page

            try {
                data_state.roles.is_loading = true

                const response = await gateway.getRoles(page, { name, isExtended: 0 })
                data_state.roles.list = response.data

                data_state.roles.pagination.total_pages = response.meta.last_page

                data_state.roles.is_failed = false
                data_state.roles.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.roles.failure.catchError(error)

                if (!data_state.roles.failure.error_state.is_cancel) {
                    data_state.roles.is_failed = true
                }
            } finally {
                if (!data_state.roles.failure.error_state.is_cancel) {
                    data_state.roles.is_loading = false
                }
            }
        }

        const loadMoreRoles = async (page: number) => {
            if (page > data_state.roles.pagination.total_pages) return
            data_state.roles.pagination.current_page = page

            try {
                data_state.roles.is_loading = true

                const response = await gateway.getRoles(page, { isExtended: 0 })
                data_state.roles.list = data_state.roles.list.concat(response.data)

                data_state.roles.pagination.total_pages = response.meta.last_page

                data_state.roles.is_failed = false
                data_state.roles.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.roles.failure.catchError(error)

                if (!data_state.roles.failure.error_state.is_cancel) {
                    data_state.roles.is_failed = true
                }
            } finally {
                if (!data_state.roles.failure.error_state.is_cancel) {
                    data_state.roles.is_loading = false
                }
            }
        }

        const searchRoles = (name: string) => {
            loadRoles(1, name)
        }

        const unshiftFindedRole = async (role: GatewayRoleT) => {
            await loadRoles(data_state.roles.pagination.current_page)
            data_state.roles.list.unshift(role)
        }

        onBeforeMount(() => {
            loadRoles(1)

            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.name = props.initialValue.name
            state.isMain = props.initialValue.isMain

            if (props.initialValue.visibleToRoleIds) {
                state.roleIds = props.initialValue.visibleToRoleIds
            }
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('create', toRaw(state))
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', { id: state.id, payload: toRaw(state) })
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
        }

        return {
            state,
            data_state,
            state_form$,
            modalTitle,
            modalConfirmButtonText,
            emitCancel,
            emitManager,
            //
            loadRoles,
            loadMoreRoles,
            searchRoles,
            unshiftFindedRole,
        }
    },
})
